import {Component, OnInit} from '@angular/core';
import {MusicCollectionService} from '../../service/MusicCollectionService';
import {MusicCollectionSettingsService} from '../../service/MusicCollectionSettingsService';
import { AlbumSummaryWithArtist } from 'src/app/model/AlbumSummaryWithArtist';
import { AlbumsCollection } from 'src/app/model/AlbumsCollection';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { ArtistSummaryWithAlbums } from 'src/app/model/ArtistSummaryWithAlbums';

@Component({
  selector: 'app-album-collection',
  templateUrl: './album-collection.component.html',
  styleUrls: ['./album-collection.component.scss']
})
export class AlbumCollectionComponent implements OnInit {

  albumsCollection: AlbumsCollection;
  albumSortView: string = MusicCollectionSettingsService.ALBUM_SORT_ALPHABETICAL_BY_ARTIST_NAME_THEN_CHRONOLOGICAL;
  sortedAlbums: Array<AlbumSummaryWithArtist>;
  searchText: string;
  searchModelChanged: Subject<string> = new Subject<string>();
  searchTextForFiltering: string;

  constructor(private musicCollectionService: MusicCollectionService,
              private musicCollectionSettingsService: MusicCollectionSettingsService) {
  }

  ngOnInit(): void {
    console.log('in AlbumCollectionComponent.ngOnInit()');
    this.initializeAlbumSortView();
    this.configureSearchListener();
    this.getAlbumSummaryCollection();
  }

    getAlbumSummaryCollection(): void {
      this.musicCollectionService.getArtistSummaryWithAlbumsCollection().subscribe(result => this.processCollection(result));
    }
  
    processCollection(artistSummaryWithAlbums: Array<ArtistSummaryWithAlbums>): void {
      console.log('Processing artist summary with albums collection');
      this.albumsCollection = new AlbumsCollection();
      this.albumsCollection.deserializeArtistSummaryWithAlbums(artistSummaryWithAlbums);
      this.setSortedAlbumsBasedOnSortView();
    }
  
    initializeAlbumSortView(): void {
      this.albumSortView = this.musicCollectionSettingsService.getAlbumSortViewFromLocalStorage(
        MusicCollectionSettingsService.ALBUM_SORT_ALPHABETICAL_BY_ARTIST_NAME_THEN_CHRONOLOGICAL);
    }

    configureSearchListener(): void {
        this.searchModelChanged
        .pipe(
            debounceTime(500),
            distinctUntilChanged())
        .subscribe(model => {
            this.searchTextForFiltering = model;
        });
    }

    searchTextChanged(text: string): void {
        this.searchModelChanged.next(text);
    }

    albumSortViewIsAlphabeticalByArtistNameThenChronological(): boolean {
        return this.albumSortView === MusicCollectionSettingsService.ALBUM_SORT_ALPHABETICAL_BY_ARTIST_NAME_THEN_CHRONOLOGICAL;
    }

    albumSortViewIsArtistAlbumCountThenChronological(): boolean {
        return this.albumSortView === MusicCollectionSettingsService.ALBUM_SORT_ARTIST_ALBUM_COUNT_THEN_CHRONOLOGICAL;
    }

    albumSortViewIsReviewDate(): boolean {
        return this.albumSortView === MusicCollectionSettingsService.ALBUM_SORT_REVIEW_DATE_DESCENDING;
    }

    albumSortViewIsRatingAscending(): boolean {
        return this.albumSortView === MusicCollectionSettingsService.ALBUM_SORT_RATING_ASCENDING;
    }

    albumSortViewIsRatingDescending(): boolean {
        return this.albumSortView === MusicCollectionSettingsService.ALBUM_SORT_RATING_DESCENDING;
    }

    albumSortViewIsYearAscending(): boolean {
        return this.albumSortView === MusicCollectionSettingsService.ALBUM_SORT_YEAR_ASCENDING;
    }

    albumSortViewIsYearDescending(): boolean {
        return this.albumSortView === MusicCollectionSettingsService.ALBUM_SORT_YEAR_DESCENDING;
    }

    sortAlphabeticallyByArtistNameThenChronologically(): void {
        this.sortAlbums(MusicCollectionSettingsService.ALBUM_SORT_ALPHABETICAL_BY_ARTIST_NAME_THEN_CHRONOLOGICAL, 
                        this.albumsCollection.getAlbumsSortedAlphabeticallyByArtistNameThenChronologically());
    }

    sortByArtistAlbumCountThenChronologically(): void {
        this.sortAlbums(MusicCollectionSettingsService.ALBUM_SORT_ARTIST_ALBUM_COUNT_THEN_CHRONOLOGICAL, 
                        this.albumsCollection.getAlbumsSortedByArtistAlbumCountThenChronologically());
    }

    sortByReviewDate(): void {
        this.sortAlbums(MusicCollectionSettingsService.ALBUM_SORT_REVIEW_DATE_DESCENDING, 
                        this.albumsCollection.getAlbumsSortedByReviewDate());
    }

    sortByRatingAscending(): void {
        this.sortAlbums(MusicCollectionSettingsService.ALBUM_SORT_RATING_ASCENDING, 
                        this.albumsCollection.getAlbumsSortedByRatingAscending());
    }

    sortByRatingDescending(): void {
        this.sortAlbums(MusicCollectionSettingsService.ALBUM_SORT_RATING_DESCENDING, 
                        this.albumsCollection.getAlbumsSortedByRatingDescending());
    }

    sortByYearAscending(): void {
        this.sortAlbums(MusicCollectionSettingsService.ALBUM_SORT_YEAR_ASCENDING, 
                        this.albumsCollection.getAlbumsSortedByYearAscending());
    }

    sortByYearDescending(): void {
        this.sortAlbums(MusicCollectionSettingsService.ALBUM_SORT_YEAR_DESCENDING, 
                        this.albumsCollection.getAlbumsSortedByYearDescending());
    }

    sortAlbums(albumSortView: string, sortedAlbums: Array<AlbumSummaryWithArtist>): void {
        this.clearSearchText();
        this.albumSortView = albumSortView;
        this.sortedAlbums = sortedAlbums;
        this.musicCollectionSettingsService.storeAlbumSortViewInLocalStorage(this.albumSortView);
    }

    setSortedAlbumsBasedOnSortView(): void {
        switch (this.albumSortView) {
            case MusicCollectionSettingsService.ALBUM_SORT_ALPHABETICAL_BY_ARTIST_NAME_THEN_CHRONOLOGICAL:
                this.sortedAlbums = this.albumsCollection.getAlbumsSortedAlphabeticallyByArtistNameThenChronologically();
                break;
            case MusicCollectionSettingsService.ALBUM_SORT_ARTIST_ALBUM_COUNT_THEN_CHRONOLOGICAL:
                this.sortedAlbums = this.albumsCollection.getAlbumsSortedByArtistAlbumCountThenChronologically();
                break;
            case MusicCollectionSettingsService.ALBUM_SORT_REVIEW_DATE_DESCENDING:
                this.sortedAlbums = this.albumsCollection.getAlbumsSortedByReviewDate();
                break;
            case MusicCollectionSettingsService.ALBUM_SORT_RATING_ASCENDING:
                this.sortedAlbums = this.albumsCollection.getAlbumsSortedByRatingAscending();
                break;
            case MusicCollectionSettingsService.ALBUM_SORT_RATING_DESCENDING:
                this.sortedAlbums = this.albumsCollection.getAlbumsSortedByRatingDescending();
                break;
            case MusicCollectionSettingsService.ALBUM_SORT_YEAR_ASCENDING:
                this.sortedAlbums = this.albumsCollection.getAlbumsSortedByYearAscending();
                break;
            case MusicCollectionSettingsService.ALBUM_SORT_YEAR_DESCENDING:
                this.sortedAlbums = this.albumsCollection.getAlbumsSortedByYearDescending();
                break;
            default:
                console.log('Unexpected album sort view of ' + this.albumSortView);
                this.sortedAlbums = this.albumsCollection.getAlbumsSortedAlphabeticallyByArtistNameThenChronologically();
        }
    }

    clearSearchText(): void {
        this.searchText = '';
        this.searchTextForFiltering = '';
    }
}
